/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Typed identifiers for each configurable OnePass option row, mirroring the
 * `optionListType` values returned by the cloudcore-onepass API.
 *
 * Kept as a `const` object so the values are usable as both runtime strings
 * and compile-time types without a TypeScript `enum`.
 */
export const RecordingOptionListType = {
  /** Episodes to include ("New Only", "All Episodes", …). */
  Include: "includeType",
  /** Start-from episode selection. */
  StartFrom: "startFrom",
  /** Record type (e.g. live, HD, …). */
  RecordType: "recordType",
  /** Channel to record from. */
  Channel: "channel",
  /** Video quality preference. */
  VideoQuality: "videoQuality",
  /** Maximum number of episodes to keep. */
  KeepAtMost: "keepAtMost",
  /** How long to keep recorded episodes. */
  KeepUntil: "keepUntil",
  /** Minutes to start recording early. */
  StartRecording: "startRecording",
  /** Minutes to stop recording late. */
  StopRecording: "stopRecording",
  /** Rent or buy preference. */
  RentOrBuy: "rentOrBuy",
  /** Always show the overlay before recording. */
  AlwaysShowOverlay: "alwaysShowOverlay"
} as const;

/** Union of all valid OnePass option list type strings. */
export type RecordingOptionListType =
  (typeof RecordingOptionListType)[keyof typeof RecordingOptionListType];

/**
 * A single configurable recording option shown as a row in the overlay.
 *
 * The first four fields drive the presentational row; the trailing optional
 * fields carry the engine round-trip data (option identity, the selectable
 * value labels and the current index) so the user's selection can be mapped
 * back to the backend submission shape on Save. They are optional so hand-built
 * sample rows remain valid.
 */
export interface RecordingOption {
  /** Stable identifier for this row — the OnePass optionListType value. */
  optionListType: RecordingOptionListType;
  /** Primary label shown on the left of the row (e.g. "Episodes"). */
  label: string;
  /** Supporting text shown beneath the label. */
  description: string;
  /** Currently selected value shown on the right (e.g. "New Only"). */
  value: string;
  /** Ordered labels of the selectable values for this row. */
  horizontalOptions?: string[];
  /**
   * Display-only labels for the horizontal value picker — unit suffixes
   * stripped so only the core value shows (e.g. "10 minutes early" → "10",
   * "All recordings" → "All"). Parallel to `horizontalOptions`; the original
   * labels are kept intact for the engine save round-trip.
   */
  displayOptions?: string[];
  /** Index of the currently selected value within `horizontalOptions`. */
  selectedIndex?: number;
  /** True when another row's selection disables this one. */
  disabled?: boolean;
}

/** Which cluster of controls currently owns D-pad focus. */
export type SeriesRecordingFocusZone = "rows" | "actions";

/** Fully describes what is focused at any moment. */
export interface SeriesRecordingFocus {
  /** The focused cluster: the option rows or the Save/Cancel actions. */
  zone: SeriesRecordingFocusZone;
  /** Index within the focused cluster. */
  index: number;
}

/** How the overlay was dismissed ("error" — the save request failed). */
export type SeriesRecordingDismissReason = "save" | "cancel" | "error" | "";

/**
 * Whether the overlay is scheduling a brand-new OnePass ("create", POST) or
 * editing an existing one ("modify", PUT). Drives which interactor call the
 * Save action issues.
 */
export type SeriesRecordingMode = "create" | "modify";

/** Tracks which option is currently active and where focus sits within it. */
export interface ActiveOptionState {
  /** The option that is currently active, or "" when none is active. */
  optionListType: RecordingOptionListType | "";
  /** Index within the option's horizontalOptions that currently has focus. */
  focusIndex: number;
  /**
   * Index of the tentatively ticked item in the VerticalListPicker.
   * Starts at option.selectedIndex when the option activates, updates only on
   * explicit NAV_OK or mouse click (not on hover/dpad focus move).
   * Written to option.selectedIndex only when the OK button is confirmed.
   */
  pendingIndex: number;
  /**
   * Index of the focused action button (0 = OK, 1 = Cancel), or null when
   * focus is inside the list. Only used by the VerticalListPicker.
   */
  actionFocusIndex: number | null;
}

/**
 * Configuration for showing the Series Recording Options overlay.
 */
export interface SeriesRecordingOptionsConfig {
  /** Heading shown at the top (e.g. "Yellowstone Record Options"). */
  title: string;
  /** Unified collection ID of the series/OnePass being modified. */
  ucid?: string;
  /**
   * Whether Save creates a new OnePass (POST) or updates an existing one
   * (PUT). Defaults to "modify" when omitted.
   */
  mode?: SeriesRecordingMode;
  /** Ordered list of options to render as rows. */
  options: RecordingOption[];
  /** Label for the confirm/Save button. */
  saveButtonLabel: string;
  /** Label for the dismiss/Cancel button. */
  cancelButtonLabel: string;
  /** Optional optionListType of the option that should receive initial focus. */
  initialFocusedOptionId?: RecordingOptionListType;
}

/**
 * State interface for the SeriesRecordingOptions feature slice.
 */
export interface SeriesRecordingOptionsState {
  /** Whether the overlay is currently visible. */
  isVisible: boolean;
  /** Heading shown at the top. */
  title: string;
  /** Unified collection ID of the series/OnePass being modified. */
  ucid: string;
  /** Whether Save creates a new OnePass (POST) or updates an existing one (PUT). */
  mode: SeriesRecordingMode;
  /** Options rendered as rows. */
  options: RecordingOption[];
  /** Label for the confirm/Save button. */
  saveButtonLabel: string;
  /** Label for the dismiss/Cancel button. */
  cancelButtonLabel: string;
  /** Currently focused control for D-pad navigation. */
  focus: SeriesRecordingFocus;
  /** Id of the option last activated (opens its value selector). */
  activatedOptionId: string;
  /** How the overlay was dismissed ("save" | "cancel" | ""). */
  dismissReason: SeriesRecordingDismissReason;
  /** State of the currently active option (closed when optionListType is ""). */
  activeOption: ActiveOptionState;
}
